import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import {
    otherCostVariablesGroupFilterGql,
    otherCostVariablesNameFilterGql,
    otherCostVariablesSubsidiaryNameFilterGql,
} from 'pages/Catalogs/Common/CatalogTable/catalogQueries';
import type { VariableIdType } from 'pages/Catalogs/Common/types';
import { Loader } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import { CatalogVariablesOtherRowMenu } from '../components/CatalogVariablesOtherRowMenu';
import type { CatalogVariablesOtherRow } from '../types';
import { getVariableSourceLabel } from '../utils';

const TABLE_ID = 'catalogVariablesOther';

interface IUseCatalogVariablesOtherColumns {
    onEdit: (id: VariableIdType) => () => void;
    onView: (id: number) => () => void;
    onRemove: (id: Exclude<VariableIdType, undefined>) => () => void;
    catalogsRules?: IUseAccess['catalogs'];
    showSubsidiaryName?: boolean;
    catalogUniqueId?: string;
    globalOnly: boolean;
    methodology: ProjectMethodologyEnum;
    setFilterValues: Dispatch<SetStateAction<Record<string, string[]>>>;
}

export const useCatalogVariablesOtherColumns = ({
    onEdit,
    onView,
    onRemove,
    catalogsRules,
    showSubsidiaryName = false,
    catalogUniqueId,
    globalOnly,
    methodology,
    setFilterValues,
}: IUseCatalogVariablesOtherColumns) => {
    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    const filterVariables = useMemo(
        () => (catalogUniqueId ? { catalogUniqueId, globalOnly, methodology } : undefined),
        [catalogUniqueId, globalOnly, methodology],
    );

    const columns = useMemo<ColDef<CatalogVariablesOtherRow>[]>(
        () => [
            {
                field: 'rowActions' as any,
                headerName: '',
                width: 48,
                maxWidth: 48,
                pinned: 'left',
                lockPosition: 'left',
                sortable: false,
                filter: false,
                resizable: false,
                suppressMovable: true,
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data?: CatalogVariablesOtherRow }) =>
                    data ? (
                        <CatalogVariablesOtherRowMenu
                            row={data}
                            onEdit={onEdit}
                            onView={onView}
                            onRemove={onRemove}
                            catalogsRules={catalogsRules}
                        />
                    ) : (
                        <Loader size="s" />
                    ),
            },
            {
                field: 'name',
                headerName: 'Наименование переменной',
                flex: 2,
                minWidth: 280,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogOtherCostVariablesNameFilter',
                    customRequestId: `otherCostVariablesNameFilter_${methodology}_${globalOnly}`,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Наименование переменной',
                            field: 'value',
                            checkboxSelection: true,
                            flex: 1,
                        },
                    ],
                    query: otherCostVariablesNameFilterGql,
                },
            },
            {
                field: 'groupName',
                headerName: 'Группа переменных',
                flex: 1,
                minWidth: 220,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogOtherCostVariablesGroupFilter',
                    customRequestId: `otherCostVariablesGroupFilter_${methodology}_${globalOnly}`,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    variables: filterVariables,
                    columns: [
                        {
                            headerName: 'Группа переменных',
                            field: 'value',
                            checkboxSelection: true,
                            flex: 1,
                        },
                    ],
                    query: otherCostVariablesGroupFilterGql,
                },
            },
            {
                colId: 'dataSourceOrFormula',
                headerName: 'Источник данных / Формула расчета',
                flex: 2,
                minWidth: 320,
                sortable: false,
                filter: false,
                valueGetter: ({ data }) => getVariableSourceLabel(data),
            },
            ...(showSubsidiaryName
                ? [
                      {
                          field: 'subsidiaryName' as const,
                          headerName: 'Дочернее общество',
                          flex: 1,
                          minWidth: 220,
                          sortable: true,
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              hideTableHeader: true,
                              changeFilterHandler,
                              dataAccessor: 'catalogOtherCostVariablesSubsidiaryNameFilter',
                              customRequestId: `otherCostVariablesSubsidiaryNameFilter_${methodology}_${globalOnly}`,
                              whereVariables: ['value'],
                              childFieldAccessor: 'value',
                              variables: filterVariables,
                              columns: [
                                  {
                                      headerName: 'Дочернее общество',
                                      field: 'value',
                                      checkboxSelection: true,
                                      flex: 1,
                                  },
                              ],
                              query: otherCostVariablesSubsidiaryNameFilterGql,
                          },
                      },
                  ]
                : []),
        ],
        [
            catalogsRules,
            changeFilterHandler,
            filterVariables,
            globalOnly,
            methodology,
            onEdit,
            onRemove,
            onView,
            showSubsidiaryName,
        ],
    );

    const [selection, , , setSelection] = useSelection(TABLE_ID);
    const [pinnedItems, , , setPinned] = usePin(TABLE_ID);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce<ColDef[]>((acc, column) => {
                if (column.field === ('rowActions' as any)) {
                    return acc;
                }

                const settingsColumn = {
                    ...column,
                    disabled: column.field === 'name',
                    pinned: pinnedItems[getColumnId(column)] || column.pinned,
                } as ColDef & { disabled: boolean };

                acc.push(settingsColumn);

                return acc;
            }, []),
        [columns, pinnedItems],
    ) as any[];

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const columnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return columnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
